/**
 * Application route paths
 * Provides type-safe route definitions
 */

export const ROUTES = {
  HOME: '/',
  ONBOARDING: '/onboarding',
  LOGIN: '/login',
  REGISTER: '/register',
  SETTINGS: '/settings',

  AI_CHAT: {
    ROOT: '/ai-chat',
    HISTORY: '/ai-chat/history',
    SETTINGS: '/ai-chat/settings',
  },

  DATA_EXPLORER: {
    ROOT: '/data-explorer',
    CHAIN_ROOT: '/data-explorer/:chain',
    TOKEN_EXPLORER: '/data-explorer/tokens',
    SOL_TOKEN_EXPLORER: '/data-explorer/sol/tokens',
    CHAIN_TOKEN_EXPLORER: '/data-explorer/:chain/tokens',
    WALLET_PROFILE: '/data-explorer/wallet/:walletAddress',
    CHAIN_WALLET_PROFILE: '/data-explorer/:chain/wallet/:walletAddress',
    TOKEN_DETAIL: '/data-explorer/token/:tokenAddress',
    CHAIN_TOKEN_DETAIL: '/data-explorer/:chain/token/:tokenAddress',
    MARKET_DATA: '/data-explorer/market-data',
    ANALYTICS: '/data-explorer/analytics',
    REPORTS: '/data-explorer/reports',
    MY_LISTS: '/data-explorer/my-lists',
    LIST_DETAIL: '/data-explorer/lists/:id',
  },

  TRADING: {
    ROOT: '/trading',
    SPOT: '/trading/spot',
    FUTURES: '/trading/futures',
    PORTFOLIO: '/trading/portfolio',
    DASHBOARD: '/trading/dashboard',
    GOOD_TRADERS: '/trading/dashboard/good_traders',
    GOOD_TRADERS_TOKEN: '/trading/dashboard/good_traders/token/:address',
    POSITIONS: '/trading/positions',
    POSITION_TOKEN: '/trading/positions/token/:address',
    STRATEGIES: '/trading/strategies',
    /** Admin-only cross-user strategy performance overview */
    ADMIN_STRATEGY_PERFORMANCE: '/trading/strategies/performance',
    STRATEGY_DETAIL: '/trading/strategies/:id',
    STRATEGY_EDITOR: '/trading/strategy_editor/:id',
    STRATEGY_HELD_TOKENS: '/trading/strategies/:id/held_tokens',
    STRATEGY_TOKEN_TRADES: '/trading/strategies/:id/token_trades/:tokenAddress',
    STRATEGY_TEMPLATES: '/trading/strategies/templates',
    HOLDINGS: '/trading/holdings',
  },
} as const;

/**
 * Route sitemap for global chain switching. Routes listed here have an
 * equivalent page when selected-chain state changes or when only the route
 * `:chain` param needs to be regenerated.
 */
export const CHAIN_SELECTOR_ROUTE_SITEMAP = {
  preserveSelectedChainState: [
    ROUTES.TRADING.ROOT,
    ROUTES.TRADING.DASHBOARD,
    ROUTES.TRADING.GOOD_TRADERS,
    ROUTES.TRADING.GOOD_TRADERS_TOKEN,
    ROUTES.TRADING.POSITIONS,
    ROUTES.TRADING.POSITION_TOKEN,
    ROUTES.TRADING.HOLDINGS,
    ROUTES.TRADING.STRATEGIES,
    ROUTES.TRADING.STRATEGY_DETAIL,
    ROUTES.TRADING.STRATEGY_EDITOR,
    ROUTES.TRADING.STRATEGY_TOKEN_TRADES,
    ROUTES.TRADING.STRATEGY_TEMPLATES,
    ROUTES.DATA_EXPLORER.ROOT,
    ROUTES.DATA_EXPLORER.TOKEN_EXPLORER,
    ROUTES.DATA_EXPLORER.WALLET_PROFILE,
    ROUTES.DATA_EXPLORER.TOKEN_DETAIL,
    ROUTES.DATA_EXPLORER.MY_LISTS,
    ROUTES.DATA_EXPLORER.LIST_DETAIL,
  ],
  rewriteChainParam: [
    ROUTES.DATA_EXPLORER.CHAIN_ROOT,
    ROUTES.DATA_EXPLORER.CHAIN_TOKEN_EXPLORER,
    ROUTES.DATA_EXPLORER.CHAIN_WALLET_PROFILE,
    ROUTES.DATA_EXPLORER.CHAIN_TOKEN_DETAIL,
  ],
} as const;

/**
 * Build route with parameters
 * @param path - Route path
 * @param params - Route parameters
 * @returns Formatted route path
 *
 * @example
 * buildRoute('/trading/:symbol', { symbol: 'BTC-USD' })
 * // Returns: '/trading/BTC-USD'
 */
export const buildRoute = (path: string, params?: Record<string, string | number>): string => {
  if (!params) return path;

  let result = path;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, String(value));
  });

  return result;
};

/**
 * Navigation menu structure
 */
export const NAV_ITEMS = [
  {
    label: 'Trading',
    icon: 'ChartBarIcon',
    path: ROUTES.TRADING.ROOT,
    children: [
      { label: 'Spot Trading', path: ROUTES.TRADING.SPOT },
      { label: 'Futures', path: ROUTES.TRADING.FUTURES },
      { label: 'Portfolio', path: ROUTES.TRADING.PORTFOLIO },
    ],
  },
  {
    label: 'Data Explorer',
    icon: 'ChartPieIcon',
    path: ROUTES.DATA_EXPLORER.ROOT,
    children: [
      { label: 'Market Data', path: ROUTES.DATA_EXPLORER.MARKET_DATA },
      { label: 'Analytics', path: ROUTES.DATA_EXPLORER.ANALYTICS },
      { label: 'Reports', path: ROUTES.DATA_EXPLORER.REPORTS },
    ],
  },
  {
    label: 'AI Chat',
    icon: 'ChatBubbleLeftRightIcon',
    path: ROUTES.AI_CHAT.ROOT,
    children: [
      { label: 'Chat', path: ROUTES.AI_CHAT.ROOT },
      { label: 'History', path: ROUTES.AI_CHAT.HISTORY },
    ],
  },
] as const;
